// ============================================================
// useLiveData — encapsulates all data-fetching for LiveDataComponent
// Replaces ngOnInit + getUploadedAttendanceData + fetchAttendanceCSV in Angular
// ============================================================
import { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { parseCsvToObjects, safePct } from '../shared/utils';
import districtJson from '../../../assets/jsons/district.json';

interface AttendanceData {
  // Marking status
  stumarkful: number;
  stumarpart: number;
  stumarunmar: number;
  stumarkfulper: string;
  stumarpartper: string;
  stumarunmarper: string;

  staffmark: number;
  staffunmar: number;
  staffmarkper: string;
  staffunmarper: string;

  rteschoolmark: number;
  rteschoolunmar: number;
  rteschoolmarkper: string;
  rteschoolunmarper: string;

  itkattendancemark: number;
  itkattendanceunmark: number;
  itkattendancemarkper: string;
  itkattendanceunmarper: string;

  // Attendance status
  stuattendpresent: number;
  stuattendabsent: number;
  stuattendpresentper: string;
  stuattendabsentper: string;

  teachstaffpresent: number;
  teachstaffabsent: number;
  teachstaffpresentper: string;
  teachstaffabsentper: string;

  basicteachstaffpresent: number;
  basicteachstaffabsent: number;
  basicteachstaffpresentper: string;
  basicteachstaffabsentper: string;

  rtestupresent: number;
  rtestuabsent: number;
  rtestupresentper: string;
  rtestuabsentper: string;

  itkstupresent: number;
  itkstuabsent: number;
  itkstupresentper: string;
  itkstuabsentper: string;

  lastUpdatedData: any;
}

interface LiveDataState {
  markingtable: boolean;
  attendancetable: boolean;
  stuToggle: boolean;
  stuToggles: boolean;
  graph: boolean;
  attendance: AttendanceData | null;
  loading: boolean;
  error: string | null;
  // CSV table data
  masterDataStudentMarking: any[];
  masterDataStudentAttendance: any[];
  masterDataTeacherAttendance: any[];
  districtList: any[];
  // Calculated highlights (management-type breakdown)
  highlights: any | null;
}

export function useLiveData(
  usertypeid: string,
  emisUsertype1: string,
  districtId: string,
  edu_district_id: string,
  blockId: string
) {
  const [state, setState] = useState<LiveDataState>({
    markingtable: true,
    attendancetable: false,
    stuToggle: true,
    stuToggles: false,
    graph: true,
    attendance: null,
    loading: true,
    error: null,
    masterDataStudentMarking: [],
    masterDataStudentAttendance: [],
    masterDataTeacherAttendance: [],
    districtList: [],
    highlights: null,
  });

  // Determine the CloudFront attendance JSON URL based on user type
  function getAttendanceUrl(): string {
    const base = 'https://d1wpyxz35bzzz4.cloudfront.net/';
    if (['5', '15', '13', '44', '43', '51'].includes(usertypeid)) {
      return base + 'st_landing_page_lastday_attendance_json_data.json';
    }
    if (['19', '9', '43', '24', '3'].includes(usertypeid)) {
      return (
        base + `${districtId}-dt_landing_page_lastday_attendance_json_data.json`
      );
    }
    if (usertypeid === '10') {
      return (
        base +
        `${edu_district_id}-edudt_landing_page_lastday_attendance_json_data.json`
      );
    }
    if (['2', '6', '39'].includes(usertypeid)) {
      return (
        base + `${blockId}-blk_landing_page_lastday_attendance_json_data.json`
      );
    }
    return base + 'st_landing_page_lastday_attendance_json_data.json';
  }

  function calcPercents(
    raw: any,
    itkOverride?: { present: number; absent: number }
  ): AttendanceData {
    const sf = raw?.student_attendance_school_marked ?? {};
    const stf = raw?.teaching_staff_attendance_marked ?? {};
    const rte = raw?.rte_student_attendance_school_marked ?? {};
    const itk = raw?.itk_attendance_volunteer_marked ?? {};
    const stuAtt = raw?.stu_attendance_status ?? {};
    const teachAtt = raw?.teachingstaff_attendance_status ?? {};
    const basicAtt = raw?.basic_staff_attendance_status ?? {};
    const rteAtt = raw?.rte_attendance_status ?? {};
    const itkAtt = raw?.itk_stu_attendance_status ?? {};

    const stumarkful = Number(sf.Fully_Marked_School ?? 0);
    const stumarpart = Number(sf.Partially_Marked_School ?? 0);
    const stumarunmar = Number(sf.Un_Marked_School ?? 0);
    const total1 = stumarkful + stumarpart + stumarunmar;

    const staffmark = Number(stf.Marked ?? 0);
    const staffunmar = Number(stf.Un_Marked ?? 0);

    const rteschoolmark = Number(rte.Marked_schools ?? 0);
    const rteschoolunmar = Number(rte.Unmarked_schools ?? 0);

    const itkattendancemark = Number(itk.Marked_volunteer ?? 0);
    const itkattendanceunmark = Number(itk.Unmarked_volunteer ?? 0);

    const stuattendpresent = Number(stuAtt.Total_Present ?? 0);
    const stuattendabsent = Number(stuAtt.Total_Absent ?? 0);

    const teachstaffpresent = Number(teachAtt.Present ?? 0);
    const teachstaffabsent = Number(teachAtt.Absent ?? 0);

    const basicteachstaffpresent = Number(basicAtt.Total_Present ?? 0);
    const basicteachstaffabsent = Number(basicAtt.Total_Absent ?? 0);

    const rtestupresent = Number(rteAtt.total_present ?? 0);
    const rtestuabsent = Number(rteAtt.total_absent ?? 0);

    const itkstupresent = itkOverride?.present ?? Number(itkAtt.Present ?? 0);
    const itkstuabsent = itkOverride?.absent ?? Number(itkAtt.Absent ?? 0);

    return {
      stumarkful,
      stumarpart,
      stumarunmar,
      stumarkfulper: safePct(stumarkful, total1),
      stumarpartper: safePct(stumarpart, total1),
      stumarunmarper: safePct(stumarunmar, total1),
      staffmark,
      staffunmar,
      staffmarkper: safePct(staffmark, staffmark + staffunmar),
      staffunmarper: safePct(staffunmar, staffmark + staffunmar),
      rteschoolmark,
      rteschoolunmar,
      rteschoolmarkper: safePct(rteschoolmark, rteschoolmark + rteschoolunmar),
      rteschoolunmarper: safePct(
        rteschoolunmar,
        rteschoolmark + rteschoolunmar
      ),
      itkattendancemark,
      itkattendanceunmark,
      itkattendancemarkper: safePct(
        itkattendancemark,
        itkattendancemark + itkattendanceunmark
      ),
      itkattendanceunmarper: safePct(
        itkattendanceunmark,
        itkattendancemark + itkattendanceunmark
      ),
      stuattendpresent,
      stuattendabsent,
      stuattendpresentper: safePct(
        stuattendpresent,
        stuattendpresent + stuattendabsent
      ),
      stuattendabsentper: safePct(
        stuattendabsent,
        stuattendpresent + stuattendabsent
      ),
      teachstaffpresent,
      teachstaffabsent,
      teachstaffpresentper: safePct(
        teachstaffpresent,
        teachstaffpresent + teachstaffabsent
      ),
      teachstaffabsentper: safePct(
        teachstaffabsent,
        teachstaffpresent + teachstaffabsent
      ),
      basicteachstaffpresent,
      basicteachstaffabsent,
      basicteachstaffpresentper: safePct(
        basicteachstaffpresent,
        basicteachstaffpresent + basicteachstaffabsent
      ),
      basicteachstaffabsentper: safePct(
        basicteachstaffabsent,
        basicteachstaffpresent + basicteachstaffabsent
      ),
      rtestupresent,
      rtestuabsent,
      rtestupresentper: safePct(rtestupresent, rtestupresent + rtestuabsent),
      rtestuabsentper: safePct(rtestuabsent, rtestupresent + rtestuabsent),
      itkstupresent,
      itkstuabsent,
      itkstupresentper: safePct(itkstupresent, itkstupresent + itkstuabsent),
      itkstuabsentper: safePct(itkstuabsent, itkstupresent + itkstuabsent),
      lastUpdatedData: raw,
    };
  }

  // Fetch attendance JSON and 3 CSVs in parallel
  useEffect(() => {
    const isBlock = ['2', '6', '39'].includes(usertypeid);
    const isHideGraph =
      usertypeid === '2' ||
      usertypeid === '39' ||
      (emisUsertype1 === '3' && usertypeid === '15');

    // Use imported district.json directly (no fetch needed)
    const districtList = districtJson as any[];

    const parseCsv = (url: string): Promise<any[]> =>
      new Promise((resolve) => {
        Papa.parse(url, {
          download: true,
          complete: (results) => {
            const rows = parseCsvToObjects(results.data as string[][]);
            // Enrich with district short names
            rows.forEach((row) => {
              const d = districtList.find(
                (x) => String(x.id) === String(row.district_id)
              );
              if (d) row.disShtNames = d.short_name;
            });
            // Filter for private school logins
            if (usertypeid === '24' && ['5', '6'].includes(emisUsertype1)) {
              resolve(rows.filter((x) => String(x.manage_id) === '2'));
            } else {
              resolve(rows);
            }
          },
          error: () => resolve([]),
        });
      });

    // Fetch all 4 in parallel
    Promise.all([
      fetch(getAttendanceUrl())
        .then((r) => r.json())
        .catch(() => ({})),
      parseCsv('https://d3ijhv7dn0xr3b.cloudfront.net/10390.csv'),
      parseCsv('https://d3ijhv7dn0xr3b.cloudfront.net/10391.csv'),
      parseCsv('https://d3ijhv7dn0xr3b.cloudfront.net/10394.csv'),
      // Battery test CSV for block-level ITK data
      isBlock
        ? parseCsv('https://d3ijhv7dn0xr3b.cloudfront.net/10205.csv')
        : Promise.resolve([] as any[]),
    ])
      .then(
        ([
          attendanceJson,
          stuMarking,
          stuAttendance,
          teachAttendance,
          batteryTest,
        ]) => {
          // Calculate block-level ITK from battery test CSV
          let itkOverride: { present: number; absent: number } | undefined;
          if (isBlock && batteryTest.length > 0) {
            const blockRows = batteryTest.filter(
              (r: any) => String(r.block_id) === String(blockId)
            );
            itkOverride = {
              present: blockRows.reduce(
                (s: number, r: any) => s + Number(r.Present || 0),
                0
              ),
              absent: blockRows.reduce(
                (s: number, r: any) => s + Number(r.Absent || 0),
                0
              ),
            };
          }

          const attendance = calcPercents(attendanceJson, itkOverride);

          setState((prev) => ({
            ...prev,
            loading: false,
            graph: !isHideGraph,
            attendance,
            districtList,
            masterDataStudentMarking: stuMarking,
            masterDataStudentAttendance: stuAttendance,
            masterDataTeacherAttendance: teachAttendance,
          }));
        }
      )
      .catch((err) => {
        setState((prev) => ({ ...prev, loading: false, error: String(err) }));
      });
  }, [usertypeid, emisUsertype1, districtId, edu_district_id, blockId]);

  function toggleMarking() {
    setState((prev) => ({
      ...prev,
      markingtable: true,
      attendancetable: false,
      stuToggle: true,
      stuToggles: false,
    }));
  }

  function toggleAttendance() {
    setState((prev) => ({
      ...prev,
      markingtable: false,
      attendancetable: true,
      stuToggle: false,
      stuToggles: true,
    }));
  }

  return { state, toggleMarking, toggleAttendance };
}
